import { ReactNode } from 'react';
import { UpmodeLogo } from './upmode-logo';

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  children: ReactNode;
  onNavigateBack?: () => void;
}

/**
 * Reusable layout for legal pages (ToS, Privacy, etc.)
 * Follows enterprise best practices for legal document presentation
 */
export function LegalPageLayout({ 
  title, 
  lastUpdated, 
  children,
  onNavigateBack 
}: LegalPageLayoutProps) {
  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: 'var(--background)',
      position: 'relative'
    }}>
      {/* Header */}
      <header style={{
        borderBottom: '1px solid var(--border)',
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backdropFilter: 'blur(8px)',
        backgroundColor: 'rgba(var(--card-rgb), 0.95)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '24px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <UpmodeLogo width={140} height={32} />
          
          {onNavigateBack && (
            <button
              onClick={onNavigateBack}
              style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--font-weight-medium)',
                color: 'var(--spectral-violet)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px 16px',
                borderRadius: 'var(--radius)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--muted)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              ← Back
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main style={{
        maxWidth: '960px',
        margin: '0 auto',
        padding: '64px 48px 120px'
      }}>
        {/* Title Section */}
        <div style={{ marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-4xl)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--foreground)',
            marginBottom: '16px',
            letterSpacing: '-0.02em'
          }}>
            {title}
          </h1>
          <p style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)'
          }}>
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* Content */}
        <div style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-base)',
          lineHeight: 'var(--leading-relaxed)',
          color: 'var(--foreground)'
        }}>
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        borderTop: '1px solid var(--border)',
        backgroundColor: 'var(--card)',
        padding: '32px',
        marginTop: 'auto'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          textAlign: 'center',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          color: 'var(--muted-foreground)'
        }}>
          © {new Date().getFullYear()} Upmode AI. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

/**
 * Section component for legal content
 */
export function LegalSection({ 
  title, 
  children 
}: { 
  title: string; 
  children: ReactNode;
}) {
  return (
    <section style={{ marginBottom: '48px' }}>
      <h2 style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-2xl)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--foreground)',
        marginBottom: '20px',
        letterSpacing: '-0.01em'
      }}>
        {title}
      </h2>
      <div style={{
        color: 'var(--foreground)',
        lineHeight: 'var(--leading-relaxed)'
      }}>
        {children}
      </div>
    </section>
  );
}

/**
 * Subsection component for nested legal content
 */
export function LegalSubsection({ 
  title, 
  children 
}: { 
  title: string; 
  children: ReactNode;
}) {
  return (
    <div style={{ marginBottom: '24px' }}>
      <h3 style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-lg)',
        fontWeight: 'var(--font-weight-semibold)',
        color: 'var(--foreground)',
        marginBottom: '12px'
      }}>
        {title}
      </h3>
      <div style={{
        color: 'var(--foreground)',
        lineHeight: 'var(--leading-relaxed)'
      }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Paragraph component for legal text
 */
export function LegalParagraph({ children }: { children: ReactNode }) {
  return (
    <p style={{ marginBottom: '16px' }}>
      {children}
    </p>
  );
}

/**
 * List component for legal content
 */
export function LegalList({ children }: { children: ReactNode }) {
  return (
    <ul style={{
      marginBottom: '16px',
      paddingLeft: '24px',
      listStyleType: 'disc'
    }}>
      {children}
    </ul>
  );
}

/**
 * List item component
 */
export function LegalListItem({ children }: { children: ReactNode }) {
  return (
    <li style={{ marginBottom: '8px' }}>
      {children}
    </li>
  );
}